import { FC, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Item, ItemTagSummary } from '../../../app/services/api/orderManagementClient';
import { ItemTagChip } from '../../orderly-for-distributors/ItemTags/components/ItemTagChip';

const ItemTagRow: FC<{ tags: ItemTagSummary[] }> = ({ tags }) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const visible = tags.slice(0, 2);
  const overflow = tags.slice(2);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0, mb: 0 }}>
      {visible.map((tag) => (
        <ItemTagChip
          key={tag.id}
          name={tag.name}
          textColor={tag.textColor}
          backgroundColor={tag.backgroundColor}
          iconKey={tag.iconKey}
          isActive={true}
          placeholderLabel=""
        />
      ))}
      {overflow.length > 0 && (
        <Tooltip
          open={overflowOpen}
          onClose={() => setOverflowOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top"
          arrow
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, p: 0.5 }}>
              {overflow.map((tag) => (
                <ItemTagChip
                  key={tag.id}
                  name={tag.name}
                  textColor={tag.textColor}
                  backgroundColor={tag.backgroundColor}
                  iconKey={tag.iconKey}
                  isActive={true}
                  placeholderLabel=""                />
              ))}
            </Box>
          }
        >
          <Box
            onClick={(e) => { e.stopPropagation(); setOverflowOpen((v) => !v); }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 22,
              px: 1,
              borderRadius: '999px',
              backgroundColor: 'action.hover',
              fontSize: '10px',
              fontWeight: 700,
              cursor: 'pointer',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            +{overflow.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

type Props = {
  product: Item;
  currencyCode?: string;
  basketQuantity?: number;
  onAddToOrder: (product: Item) => void;
};

const ProductCard: FC<Props> = ({ product, currencyCode, basketQuantity = 0, onAddToOrder }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('storefront');
  const [imgError, setImgError] = useState(false);

  const hasImage = !!product.thumbnailUrl && !imgError;

  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode || 'GBP',
        minimumFractionDigits: 2,
      }).format(value);
    } catch {
      return `£${value.toFixed(2)}`;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF', 
        borderRadius: '5px',
        border: 'none',
        boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        padding: '8px',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.10)',
        },
      }}
    >
      {/* Image section */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={product.thumbnailUrl}
            alt={product.name}
            onError={() => setImgError(true)}
            sx={{
              width: '70%',
              height: '70%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '8px',
              backgroundColor: theme.palette.custom.gray[200],
            }}
          />
        )}
        {(product.itemTags ?? []).length > 0 && (
          <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1, maxWidth: 'calc(100% - 8px)' }}>
            <ItemTagRow tags={product.itemTags!} />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        {product.category && (
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: theme.palette.custom.gray[400], textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {product.category}
          </Typography>
        )}

        <Typography
          onClick={() => navigate(`/ofd/products/${product.id}`)}
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
            minHeight: '2.4em',
            cursor: 'pointer',
            transition: 'opacity 0.15s ease',
            '&:hover': {
              opacity: 0.6,
            },
          }}
        >
          {product.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: '6px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, alignSelf: 'flex-end' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, mr: '6px' }}>
              {formatPrice(product.price)}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme.palette.custom.gray[400], fontWeight: 400 }}>
              {product.orderableUom}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <IconButton
              onClick={(e) => { e.stopPropagation(); onAddToOrder(product); }}
              size="small"
              title={t('addToDelivery')}
              sx={{
                backgroundColor: '#1B4332',
                color: '#fff',
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: '#143728',
                },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {basketQuantity > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: (theme.palette.success as unknown as Record<string, string>)[100],
                  color: (theme.palette.success as unknown as Record<string, string>)[700],
                  borderRadius: '999px',
                  minWidth: 16,
                  height: 16,
                  fontSize: '10px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: '3px',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              >
                {basketQuantity}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductCard;
